import { onMounted, reactive, ref, Ref, watch } from 'vue'
import {
  useElementBounding,
  useIntersectionObserver,
  useWindowSize,
} from '@vueuse/core'

export enum ContextMenuPosition {
  topLeft, top, topRight,
  rightTop, right, rightBottom,
  bottomRight, bottom, bottomLeft,
  leftBottom, left, leftTop
}

type Coordinate = { x: number, y: number }

type PositionStyle = {
  display: 'none' | 'block'
  position: 'fixed'
  top?: string
  left?: string
  width?: string
  'z-index': number
}

const initPositionStyle = reactive<PositionStyle>({
  display: 'none',
  position: 'fixed',
  top: undefined,
  left: undefined,
  width: undefined,
  'z-index': 1000,
})

const useContextMenuPosition = (
  anchor: Ref<HTMLElement | null>,
  menu: Ref<HTMLElement | null>,
  positions: Ref<ContextMenuPosition[]>,
  withAnchorWidth: Ref<boolean>
) => {
  const style = initPositionStyle
  const isAnchorVisible = ref(true)

  const updatePosition = (): void => {
    const anchorRect = anchor.value?.getBoundingClientRect()
    const menuRect = menu.value?.getBoundingClientRect()

    if (anchorRect === undefined || menuRect === undefined) { return }

    const coordinate = mapAndFindCoordinate(anchorRect, menuRect)
    if (coordinate === undefined || !isAnchorVisible.value) {
      style.display = 'none'
      return
    }

    style.display = 'block'
    style.left = `${coordinate.x}px`
    style.top = `${coordinate.y}px`
    style.width = withAnchorWidth.value ? `${anchorRect.width}px` : undefined
  }

  const mapAndFindCoordinate = (anchorRect: DOMRectReadOnly, menuRect: DOMRectReadOnly): Coordinate | undefined => {
    for (const position of positions.value) {
      const coordinate = getCoordinate(position, anchorRect, menuRect)
      if (fitsInViewport(coordinate, anchorRect, menuRect)) {
        return coordinate
      }
    }
  }

  const fitsInViewport = (coordinate: Coordinate, anchorRect: DOMRectReadOnly, menuRect: DOMRectReadOnly): boolean => {
    const viewportWidth = windowSize.width.value
    const viewportHeight = windowSize.height.value
    const { height: menuHeight, width: menuWidth } = getMenuSize(anchorRect, menuRect)

    return (
      coordinate.x >= 0 &&
      coordinate.y >= 0 &&
      coordinate.x + menuWidth <= viewportWidth &&
      coordinate.y + menuHeight <= viewportHeight
    )
  }

  const getCoordinate = (position: ContextMenuPosition, anchorRect: DOMRectReadOnly, menuRect: DOMRectReadOnly): Coordinate => {
    const { height: menuHeight, width: menuWidth } = getMenuSize(anchorRect, menuRect)

    switch (position) {
    case ContextMenuPosition.topLeft:
      return { x: anchorRect.left, y: anchorRect.top - menuHeight }
    case ContextMenuPosition.top:
      return { x: anchorRect.left + (anchorRect.width - menuWidth) / 2, y: anchorRect.top - menuHeight }
    case ContextMenuPosition.topRight:
      return { x: anchorRect.right - menuWidth, y: anchorRect.top - menuHeight }
    case ContextMenuPosition.rightTop:
      return { x: anchorRect.right, y: anchorRect.top }
    case ContextMenuPosition.right:
      return { x: anchorRect.right, y: anchorRect.top + (anchorRect.height - menuHeight) / 2 }
    case ContextMenuPosition.rightBottom:
      return { x: anchorRect.right, y: anchorRect.bottom - menuHeight }
    case ContextMenuPosition.bottomRight:
      return { x: anchorRect.right - menuWidth, y: anchorRect.bottom }
    case ContextMenuPosition.bottom:
      return { x: anchorRect.left + (anchorRect.width - menuWidth) / 2, y: anchorRect.bottom }
    case ContextMenuPosition.bottomLeft:
      return { x: anchorRect.left, y: anchorRect.bottom }
    case ContextMenuPosition.leftBottom:
      return { x: anchorRect.left - menuWidth, y: anchorRect.bottom - menuHeight }
    case ContextMenuPosition.left:
      return { x: anchorRect.left - menuWidth, y: anchorRect.top + (anchorRect.height - menuHeight) / 2 }
    case ContextMenuPosition.leftTop:
      return { x: anchorRect.left - menuWidth, y: anchorRect.top }
    }
  }

  const getMenuSize = (anchorRect: DOMRectReadOnly, menuRect: DOMRectReadOnly): { height: number, width: number } => {
    return {
      height: menuRect.height,
      width: withAnchorWidth.value ? anchorRect.width : menuRect.width,
    }
  }

  const anchorBounding = useElementBounding(anchor)
  const windowSize = useWindowSize()

  watch(
    () => ({ anchorBounding, menu, positions, withAnchorWidth, windowSize }),
    updatePosition,
    { deep: true }
  )

  onMounted(() => {
    setTimeout(updatePosition)
  })

  useIntersectionObserver(anchor, ([entry]) => {
    isAnchorVisible.value = entry?.isIntersecting ?? false
    updatePosition()
  })

  return style
}

export default useContextMenuPosition
